import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { listHits, descartarHit, reportarHit, type Hit } from "@/api/hits";
import { motivosDescarte, respuestas, type MotivoDescarte, type Respuesta } from "@/api/catalogos";
import { useRealtime } from "@/hooks/useSocket";
import { useToast } from "@/contexts/ToastContext";
import { formatDate } from "@/lib/utils";

const FASES = ["pendiente", "reportado", "descartado"] as const;

export function HitsPage() {
  const [fase, setFase] = useState<(typeof FASES)[number]>("pendiente");
  const [active, setActive] = useState<Hit | null>(null);
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["hits", fase],
    queryFn: () => listHits({ fase, page: 1, size: 50 }),
    refetchInterval: 30_000,
  });

  useRealtime("hit.detected", () => {
    qc.invalidateQueries({ queryKey: ["hits"] });
  });
  useRealtime("hit.updated", () => {
    qc.invalidateQueries({ queryKey: ["hits"] });
  });

  return (
    <div className="p-6 space-y-4" data-testid="page-hits">
      <header className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">Hits</h1>
        <div role="tablist" className="flex rounded-md border border-border overflow-hidden">
          {FASES.map((f) => (
            <button
              key={f}
              type="button"
              data-testid={`hits-tab-${f}`}
              className={
                "px-3 py-1.5 text-xs " +
                (f === fase
                  ? "bg-primary text-white"
                  : "bg-surface2 text-foreground hover:bg-surface")
              }
              onClick={() => setFase(f)}
            >
              {f}
            </button>
          ))}
        </div>
        <span className="text-sm text-muted-200 ml-auto">
          {data?.total ?? 0} en cola
        </span>
      </header>

      <div className="card">
        <table className="w-full text-sm" data-testid="hits-table">
          <thead className="text-left text-xs text-muted-200">
            <tr className="border-b border-border">
              <th className="py-2">Fecha</th>
              <th>VIN</th>
              <th>Placa</th>
              <th>Unidad</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((h) => (
              <tr key={h.id} className="border-b border-border/40">
                <td className="py-2">{formatDate(h.at)}</td>
                <td className="font-mono text-xs">{h.vin ?? ""}</td>
                <td className="font-mono text-xs">{h.placa ?? ""}</td>
                <td>{h.unitId}</td>
                <td>{h.fase}</td>
                <td>
                  {h.fase === "pendiente" && (
                    <button
                      className="btn-ghost"
                      data-testid={`hit-row-${h.id}-open`}
                      onClick={() => setActive(h)}
                    >
                      Atender
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {active && (
        <HitDialog hit={active} onClose={() => setActive(null)} />
      )}
    </div>
  );
}

function HitDialog({ hit, onClose }: { hit: Hit; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<"reportar" | "descartar">("reportar");

  const respuestasQ = useQuery<Respuesta[]>({ queryKey: ["respuestas"], queryFn: respuestas.list });
  const motivosQ = useQuery<MotivoDescarte[]>({
    queryKey: ["motivos-descarte"],
    queryFn: motivosDescarte.list,
  });

  const [respuestaId, setRespuestaId] = useState<number | null>(null);
  const [motivoId, setMotivoId] = useState<number | null>(null);
  const [comentarios, setComentarios] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function reportar() {
    if (!respuestaId) return;
    setSubmitting(true);
    try {
      await reportarHit(hit.id, { respuestaId, comentarios });
      toast.push("Hit reportado", "success");
      onClose();
      await qc.invalidateQueries({ queryKey: ["hits"] });
    } catch {
      toast.push("Error al reportar", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function descartar() {
    if (!motivoId || !comentarios) return;
    setSubmitting(true);
    try {
      await descartarHit(hit.id, { motivoDescarteId: motivoId, comentarios });
      toast.push("Hit descartado", "success");
      onClose();
      await qc.invalidateQueries({ queryKey: ["hits"] });
    } catch {
      toast.push("Error al descartar", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 grid place-items-center bg-black/60 p-4"
      data-testid="hit-dialog"
    >
      <div className="card w-full max-w-lg space-y-4">
        <header className="flex items-center justify-between">
          <h2 className="font-semibold">Hit #{hit.id}</h2>
          <button onClick={onClose} className="btn-ghost">Cerrar</button>
        </header>
        <div className="text-sm text-muted-200">
          <div>VIN: <span className="font-mono">{hit.vin ?? "—"}</span></div>
          <div>Placa: <span className="font-mono">{hit.placa ?? "—"}</span></div>
          <div>Detectado: {formatDate(hit.at)}</div>
        </div>

        <div role="tablist" className="flex gap-1">
          <button
            data-testid="hit-tab-reportar"
            className={"btn " + (tab === "reportar" ? "btn-primary" : "btn-ghost")}
            onClick={() => setTab("reportar")}
          >
            Reportar
          </button>
          <button
            data-testid="hit-tab-descartar"
            className={"btn " + (tab === "descartar" ? "btn-danger" : "btn-ghost")}
            onClick={() => setTab("descartar")}
          >
            Descartar
          </button>
        </div>

        {tab === "reportar" ? (
          <>
            <select
              data-testid="hit-respuesta"
              className="input"
              value={respuestaId ?? ""}
              onChange={(e) => setRespuestaId(Number(e.target.value) || null)}
            >
              <option value="">Selecciona respuesta…</option>
              {(respuestasQ.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nombre}
                </option>
              ))}
            </select>
            <textarea
              data-testid="hit-comentarios-reportar"
              className="input"
              rows={3}
              placeholder="Comentarios"
              value={comentarios}
              onChange={(e) => setComentarios(e.target.value)}
            />
            <button
              onClick={reportar}
              disabled={!respuestaId || submitting}
              data-testid="hit-reportar-submit"
              className="btn-primary w-full"
            >
              Reportar
            </button>
          </>
        ) : (
          <>
            <select
              data-testid="hit-motivo"
              className="input"
              value={motivoId ?? ""}
              onChange={(e) => setMotivoId(Number(e.target.value) || null)}
            >
              <option value="">Selecciona motivo…</option>
              {(motivosQ.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nombre}
                </option>
              ))}
            </select>
            <textarea
              data-testid="hit-comentarios-descartar"
              className="input"
              rows={3}
              placeholder="Motivo (obligatorio)"
              value={comentarios}
              onChange={(e) => setComentarios(e.target.value)}
            />
            <button
              onClick={descartar}
              disabled={!motivoId || !comentarios || submitting}
              data-testid="hit-descartar-submit"
              className="btn-danger w-full"
            >
              Descartar con motivo
            </button>
          </>
        )}
      </div>
    </div>
  );
}
